import {ApplicationRef, Injectable} from '@angular/core';
import {Store} from '@ngxs/store';
import {AccessType, ConnectionStatus, ViewportTab} from '@app/modules/shared/data/constants';
import {LocalUser, LocalUserAction, LocalUserState} from '@app/state/local-user.state';
import {ClassroomAction, ClassroomState, ClassroomStateModel} from '@app/state/classroom-state.service';
import {AssignmentAction, AssignmentState, AssignmentStateModel} from '@app/state/assignment.state';
import {interval, Observable, Observer, of, Subject} from 'rxjs';
import {catchError, concatMap, map, takeUntil} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {MessageHandler} from '@app/services/message-handler';
import {
  ClassroomMessage,
  DataService,
  MessageRecord,
  MsgRecordType,
  RecordArgs,
  RecordStack
} from '@app/services/data.service';
import {
  RemoteUserAction,
  RemoteUserAdditionalInfoStateModel,
  RemoteUserInfoStateModel,
  RemoteUsersInfoState
} from '@app/state/remote-users.state';
import {AssignmentCommands, AssignmentEvents, AssignmentStates} from '@app/models/commands/assignment-commands';
import {WhiteboardService} from '@app/modules/whiteboard/services/whiteboard.service';
import {ModuleService} from '@app/services/module-service';
import {WebPortalMessagingService} from '@app/services/web-portal-messaging.service';
import {ReportAction} from '@app/state/report.state';

@Injectable({
  providedIn: 'root'
})
export class AssignmentService implements MessageHandler, ModuleService {
  private dataService: DataService;
  private webPortalMessagingService: WebPortalMessagingService;
  private localUser: LocalUser;
  private classroom: ClassroomStateModel;
  public assignment: AssignmentStateModel;
  private hideMessageSubject$ = new Subject();
  private remoteUsers: { [key: number]: RemoteUserAdditionalInfoStateModel } = {};

  public handleRecordStack: (recordStack: RecordStack) => Promise<void>; /* should be set in ChatCommands */

  constructor(
    private store: Store,
    private http: HttpClient,
    private whiteboardService: WhiteboardService,
    private applicationRef: ApplicationRef
  ) {
    this.listenStoreChanges();
  }

  public setWebPortalMessagingService(webPortalMessagingService: WebPortalMessagingService): void {
    this.webPortalMessagingService = webPortalMessagingService;
  }
  public setDataService(dataService: DataService): void {
    this.dataService = dataService;
  }

  private listenStoreChanges() {
    this.store.select(LocalUserState).subscribe(currentUser => {
      this.localUser = currentUser;
    });

    this.store.select(ClassroomState).subscribe(roomState => {
      this.classroom = roomState;
    });

    this.store.select(RemoteUsersInfoState).subscribe(({usersInfoById}: RemoteUserInfoStateModel) => {
      this.remoteUsers = usersInfoById;
    });


    this.store.select(AssignmentState).subscribe((assignment: AssignmentStateModel) => {
      this.assignment = assignment;
    });
  }

  public init(): void {
    this.getState().subscribe();
  }

  public getState(): Observable<any> {
    this.store.dispatch(new LocalUserAction.SetCurrentTab(ViewportTab.WHITEBOARD));
    this.store.dispatch(new AssignmentAction.UnloadAssignment());
    this.store.dispatch(new AssignmentAction.DisallowSwitchAccessForAll());
    this.store.dispatch(new RemoteUserAction.SetStudentsCurrentTab(ViewportTab.WHITEBOARD));
    this.store.dispatch(new ClassroomAction.SetBoardTab(ViewportTab.WHITEBOARD));
    this.hideAllStudentsAreOnWBMessage();

    return this.http.get(`${this.classroom.vcrURL}/assignment/get-state`)
      .pipe(map((result: any) => {
        console.log(`GET STATE - ${result.message}`);
        this.handleRecordStack(result.data as RecordStack).then();

        this.assignmentStateChanged();
      }));
  }

  private getAssignmentUrlForStaffMember(groupAssignmentId, isGroupAssignment): Observable<string> {
    if (isGroupAssignment) {
      return this.http.get(`${this.classroom.vcrURL}/assignment/assignment-report-url`, {
        params: { groupAssignmentId }
      }).pipe(map((response: any) => {
        return response.data;
      }));
    } else {
      const url = `${this.classroom.cmsURL}/service/getassignmentreporturi?GroupAssignmentId=${groupAssignmentId}&isGroupAssignmentReport=${isGroupAssignment}&LcmTeacherId=${this.localUser.id}`;

      return this.http.get(url, {responseType: 'text'}).pipe(concatMap(response => {
        response = response.replace(/\s\n/, '');
        const vcPathInfo = encodeURIComponent(`/${this.classroom.roomNum}${this.classroom.vcrURL}/whiteboard/external?roomNum=${this.classroom.roomNum}#`);
        const reportUrl = `${this.classroom.cmsURL + response}&uid=${this.localUser.id}&vchostport=${location.host}&vcpathinfo=${vcPathInfo}&order=0&assignmentNavigation=false&timestamp=${new Date().getTime()}`;
        console.log('Loading teacher assignment   ' + reportUrl + '');
        return of(reportUrl);
      }));
    }
  }

  public sendLoad(assignmentId?: number): Observable<any> {
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        AssignmentStates.LOAD,
        new RecordArgs(assignmentId + '')
      )
    };
    return this.send(`${this.classroom.vcrURL}/assignment/t`, message);
  }
  public onLoad(record: MessageRecord) {
    if (this.localUser.isGuest) {
      return;
    }

    const assignmentId: number = parseInt(record.data);
    //need to set assignment pending status in case
    this.store.dispatch(new RemoteUserAction.SetStudentsCurrentTab(ViewportTab.ASSIGNMENT));
    this.store.dispatch(new ClassroomAction.SetBoardTab(ViewportTab.ASSIGNMENT));
    this.store.dispatch(new LocalUserAction.SetCurrentTab(ViewportTab.ASSIGNMENT));

    if (this.localUser.accessType !== AccessType.STUDENT) {
      this.getAssignmentUrlForStaffMember(assignmentId, true)
        .pipe(catchError((): Observable<void> => {
          this.store.dispatch(new ReportAction.Load('', assignmentId));
          return;
        }))
        .subscribe((reportURL: string): void => {
          this.store.dispatch(new ReportAction.Load(reportURL, assignmentId));
        });
    }

    this.http.get(`${this.classroom.vcrURL}/assignment/assignment-url`, {
      params: {
        groupAssignmentId: assignmentId,
      }
    }).subscribe((response: {data: string}): void => {
      let assignmentURL: string = response.data;
      if (this.localUser.accessType === AccessType.TEACHER) {
        if (assignmentURL.includes('?')) {
          assignmentURL += '&auth_mode=staff';
        } else {
          assignmentURL += '?auth_mode=staff';
        }
      }

      this.store.dispatch(new AssignmentAction.LoadAssignment(assignmentURL, assignmentId));

      this.assignmentStateChanged();
    });
  }

  public sendUnload(resetSwitchAccess: boolean = true): Observable<any> {
    if (this.assignment.loadedAssignmentId > 0) {
      const message: ClassroomMessage = {
        store: false,
        broadcast: true,
        selfProcess: true,
        mirrorState: AssignmentStates.LOAD,
        record: this.buildRecord(
          AssignmentStates.UNLOAD,
          new RecordArgs(this.assignment.loadedAssignmentId + '')
        )
      };

      if (resetSwitchAccess) {
        const switchMessage: ClassroomMessage = {
          store: false,
          broadcast: true,
          selfProcess: true,
          mirrorState: AssignmentStates.ASSIGNMENT_SWITCH_FOR_ALL,
          record: this.buildRecord(
            AssignmentStates.DISALLOW_ASSIGNMENT_SWITCH_FOR_ALL,
            new RecordArgs(JSON.stringify({ switchToAssignment: false }))
          )
        };

        return new Observable<void>((observer: Observer<void>) => {
          this.send(`${this.classroom.vcrURL}/assignment/del`, switchMessage).subscribe(() => {
            this.send(`${this.classroom.vcrURL}/assignment/del`, message).subscribe(() => {
              observer.next();
            });
          });
        });
      }

      return this.send(`${this.classroom.vcrURL}/assignment/del`, message);
    } else {
      return of(true);
    }
  }
  public onUnload(_: MessageRecord) {
    this.store.dispatch(new LocalUserAction.SetCurrentTab(ViewportTab.WHITEBOARD));
    this.store.dispatch(new AssignmentAction.UnloadAssignment());
    this.store.dispatch(new RemoteUserAction.SetStudentsCurrentTab(ViewportTab.WHITEBOARD));
    this.store.dispatch(new ClassroomAction.SetBoardTab(ViewportTab.WHITEBOARD));
    this.hideAllStudentsAreOnWBMessage();

    this.applicationRef.tick(); /* force update ui to make whiteboard wrapper visible */
    this.whiteboardService.updateGrid();
    this.whiteboardService.updateScroll();

    this.assignmentStateChanged();
  }

  public sendUsersRefresh(): Observable<any> {
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        AssignmentEvents.REFRESH_USERS_ASSIGNMENT,
        new RecordArgs('')
      )
    };
    return this.send(`${this.classroom.vcrURL}/assignment/t`, message);
  }
  public onUsersRefresh() {
    if (this.localUser.accessType === AccessType.STUDENT) {
      this.store.dispatch(new AssignmentAction.RefreshAssignment());
    }
  }

  public sendRefresh(): Observable<any> {
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        AssignmentEvents.REFRESH,
        new RecordArgs('')
      )
    };
    return this.send(`${this.classroom.vcrURL}/assignment/t`, message);
  }
  public onRefresh() {
    this.store.dispatch(new ClassroomAction.SetBoardTab(ViewportTab.ASSIGNMENT));
    this.store.dispatch(new LocalUserAction.SetCurrentTab(ViewportTab.ASSIGNMENT));
    this.store.dispatch(new AssignmentAction.RefreshAssignment());
  }

  public sendLastAssignmentReport(assignmentId: number): Observable<any> {
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        AssignmentStates.LAST_ASSIGNMENT_REPORT,
        new RecordArgs(assignmentId + '')
      )
    };
    return this.send(`${this.classroom.vcrURL}/assignment/t`, message);
  }
  public onLastAssignmentReport(record: MessageRecord) {
    const assignmentId = Number(record.data);
    if (this.localUser.accessType !== AccessType.STUDENT) {
      this.getAssignmentUrlForStaffMember(assignmentId, false).subscribe(url => {
        window.open(url, '_blank', 'height=800,width=800');
      });
    }
  }

  public sendAllowSwitchFor(userId: number): Observable<any> {
    const recordData: string = JSON.stringify([userId]);

    const students = Object
      .values(this.remoteUsers)
      .filter((remoteUser): boolean => remoteUser.accessType === AccessType.STUDENT)
      .filter((remoteUser): boolean => remoteUser.roomConnectionStatus === ConnectionStatus.ONLINE);

    const allowedStudentsCount: number = this.assignment.allowedSwitchingUsers?.size || 0;

    if (allowedStudentsCount === students.length - 1) {
      return this.sendAllowSwitchForAll();
    } else {
      const message: ClassroomMessage = {
        store: true,
        broadcast: true,
        selfProcess: true,
        mirrorState: AssignmentStates.ASSIGNMENT_SWITCH_FOR_ALL,
        record: this.buildRecord(
          AssignmentStates.ALLOW_SWITCHER_FOR,
          new RecordArgs(recordData)
        )
      };
      return this.send(`${this.classroom.vcrURL}/assignment/add`, message);
    }
  }
  public onAllowSwitcherAccessFor(record: MessageRecord): void {
    const switcherAllowedUserIds: number[] = JSON.parse(record.data);

    switcherAllowedUserIds.forEach((userId: number): void => {
      this.store.dispatch(new AssignmentAction.AllowSwitchAccessFor(userId));
    });
  }

  public sendDisallowSwitchFor(userId: number): Observable<any> {
    const recordData: string = JSON.stringify([userId]);

    if (this.assignment.allowedSwitchingUsers?.size === 1) {
      return this.sendDisallowSwitchForAll();
    } else {
      const message: ClassroomMessage = {
        store: true,
        broadcast: true,
        selfProcess: true,
        mirrorState: AssignmentStates.ASSIGNMENT_SWITCH_FOR_ALL,
        record: this.buildRecord(
          AssignmentStates.DISALLOW_SWITCHER_FOR,
          new RecordArgs(recordData)
        )
      };
      return this.send(`${this.classroom.vcrURL}/assignment/sub`, message);
    }
  }
  public onDisallowSwitcherAccessFor(record: MessageRecord): void {
    const switcherAllowedUserIds: number[] = JSON.parse(record.data);

    switcherAllowedUserIds.forEach((userId: number): void => {
      this.store.dispatch(new AssignmentAction.DisallowSwitchAccessFor(userId));
    });

    if (this.localUser.accessType === AccessType.STUDENT) {
      this.store.dispatch(new ClassroomAction.SetBoardTab(ViewportTab.ASSIGNMENT));
      this.store.dispatch(new LocalUserAction.SetCurrentTab(ViewportTab.ASSIGNMENT));
    }
  }

  public sendAllowSwitchForAll(): Observable<any> {
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        AssignmentStates.ASSIGNMENT_SWITCH_FOR_ALL,
        new RecordArgs('[]')
      )
    };
    return this.send(`${this.classroom.vcrURL}/assignment/t`, message);
  }
  public onAllowSwitchForAll(record: MessageRecord) {
    const userIds: number[] = JSON.parse(record.data);

    if (userIds.length === 0) {
      this.store.dispatch(new AssignmentAction.AllowSwitchAccessForAll());
    } else {
      userIds.forEach((userId: number) => {
        this.store.dispatch(new AssignmentAction.AllowSwitchAccessFor(userId));
      });
    }

    if (this.localUser.accessType === AccessType.STUDENT) {
      this.store.dispatch(new ClassroomAction.SetBoardTab(ViewportTab.ASSIGNMENT));
      this.store.dispatch(new LocalUserAction.SetCurrentTab(ViewportTab.ASSIGNMENT));
    }
    this.toggleShowMessageForUnloadAssignmentIfThereIsNeed();
  }

  public sendDisallowSwitchForAll(): Observable<any> {
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      selfProcess: true,
      mirrorState: AssignmentStates.ASSIGNMENT_SWITCH_FOR_ALL,
      record: this.buildRecord(
        AssignmentStates.DISALLOW_ASSIGNMENT_SWITCH_FOR_ALL,
        new RecordArgs(JSON.stringify({ switchToAssignment: true }))
      )
    };
    return this.send(`${this.classroom.vcrURL}/assignment/del`, message);
  }
  public onDisallowSwitchForAll(record: MessageRecord) {
    const recordData: { switchToAssignment: boolean } = JSON.parse(record.data);

    if (recordData.switchToAssignment && this.localUser.accessType === AccessType.STUDENT) {
      this.store.dispatch(new ClassroomAction.SetBoardTab(ViewportTab.ASSIGNMENT));
      this.store.dispatch(new LocalUserAction.SetCurrentTab(ViewportTab.ASSIGNMENT));
      this.sendViewPort(ViewportTab.ASSIGNMENT).subscribe();
    }

    this.store.dispatch(new AssignmentAction.DisallowSwitchAccessForAll());
    this.hideAllStudentsAreOnWBMessage();
  }

  public sendViewPort(index: ViewportTab): Observable<any> {
    const recordData: { [key: number]: ViewportTab } = {};
    recordData[this.localUser.id] = index;
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        AssignmentStates.VIEW_PORT,
        new RecordArgs(JSON.stringify(recordData))
      )
    };
    return this.send(`${this.classroom.vcrURL}/assignment/b-view-port`, message);
  }
  public onViewPort(record: MessageRecord) {
    const recordData: { [key: number]: ViewportTab } = JSON.parse(record.data);
    Object.keys(recordData).forEach((userId: string) => {
      const id: number = parseInt(userId);
      const tabIndex: ViewportTab = recordData[id] as ViewportTab;
      if (this.localUser.id === id) {
        this.store.dispatch(new ClassroomAction.SetBoardTab(tabIndex));
        this.store.dispatch(new LocalUserAction.SetCurrentTab(tabIndex));

        this.applicationRef.tick(); /* force update ui to make whiteboard wrapper visible */
        this.whiteboardService.updateScroll();
        this.whiteboardService.updateGrid();
      } else {
        this.store.dispatch(new RemoteUserAction.SetStudentCurrentTab(id, tabIndex));
      }
    });
    this.toggleShowMessageForUnloadAssignmentIfThereIsNeed();
  }

  public areAllStudentsSwitchToWhiteBoard(): boolean {
    return Object.keys(this.remoteUsers)
      .map((userId: string) => this.remoteUsers[userId])
      /* remove nulls and undefined */
      .filter((user?: RemoteUserAdditionalInfoStateModel): boolean => user !== null && user !== undefined)
      /* remove guests */
      .filter((user: RemoteUserAdditionalInfoStateModel): boolean => !user.isGuest)
      /* keep only online students */
      .filter((user: RemoteUserAdditionalInfoStateModel): boolean =>
        user.roomConnectionStatus === ConnectionStatus.ONLINE &&
        user.accessType === AccessType.STUDENT
      )
      /* keep only users who are switched to assignment */
      .filter((user: RemoteUserAdditionalInfoStateModel): boolean =>
        user.currentSelectedTab === ViewportTab.ASSIGNMENT
      ).length === 0;
  }

  public toggleShowMessageForUnloadAssignmentIfThereIsNeed() {
    if (this.localUser.accessType !== AccessType.STUDENT) {
      if (this.areAllStudentsSwitchToWhiteBoard() && this.assignment.allowedSwitchingUsers !== null) {
        if (!this.classroom.showAllStudentsAreOnWB) {
          this.store.dispatch(new ClassroomAction.ToggleShowAllStudentsAreOnWB(true));
          this.hideMessageSubject$.next(0);
          interval(10000).pipe(takeUntil(this.hideMessageSubject$)).subscribe(() => this.hideAllStudentsAreOnWBMessage());
        }
      } else {
        this.hideAllStudentsAreOnWBMessage();
      }
    }
  }

  public hideAllStudentsAreOnWBMessage() {
    this.hideMessageSubject$.next(0);
    this.store.dispatch(new ClassroomAction.ToggleShowAllStudentsAreOnWB(false));
  }


  private send(destination: string, data: object, options?: object): Observable<any> {
    return this.http.post(destination, data, options);
  }

  public buildRecord(command: string, args: RecordArgs): MessageRecord {
    const msgType = AssignmentStates[command] ? MsgRecordType.STATE : AssignmentEvents[command] ? MsgRecordType.EVENT : null;
    return {
      category: AssignmentCommands.category,
      name: command,
      data: args.data,
      type: msgType,
      ownerId: null, /* must be set server-side on receive */
      targetId: args.targetId
    };
  }

  private assignmentStateChanged(): void {
    if (this.localUser.accessType === AccessType.STUDENT || this.localUser.accessType === AccessType.OBSERVER) {
      return;
    }

    if (this.assignment.isLoaded) {
      this.webPortalMessagingService.sendLoadAssignment(this.assignment.loadedAssignmentId);
    } else {
      this.webPortalMessagingService.sendUnloadAssignment();
    }
  }
}
